import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icon/index.js';
import { DropdownMenu } from '../design-system/primitives/DropdownMenu/DropdownMenu.js';
import { Button } from '../design-system/primitives/Button/Button.js';
import { Avatar } from '../design-system/primitives/Avatar/Avatar.js';
import { useAuthStore } from '../../store/authStore.js';
import { resolveAvatarSrc } from '../../lib/avatar.js';
import { AdminBreadcrumbs } from './AdminBreadcrumbs.js';

type AdminTopBarProps = {
  onToggleSidebar: () => void;
};

export function AdminTopBar({ onToggleSidebar }: AdminTopBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);

  const initials =
    user?.name?.trim()?.[0]?.toUpperCase() ??
    user?.email?.trim()?.[0]?.toUpperCase() ??
    '?';

  const menuItems = useMemo(
    () => [
      {
        key: 'profile',
        label: t('nav.profile'),
        onSelect: () => navigate('/settings/profile'),
        icon: <Icon name="user" className="h-4 w-4" />,
      },
      {
        key: 'settings',
        label: t('nav.settings'),
        onSelect: () => navigate('/settings'),
        icon: <Icon name="settings" className="h-4 w-4" />,
      },
      {
        key: 'pack',
        label: t('nav.myPack'),
        onSelect: () => navigate('/settings/plan'),
        icon: <Icon name="credit-card" className="h-4 w-4" />,
      },
      {
        key: 'signout',
        label: t('nav.signOut'),
        destructive: true,
        onSelect: () => {
          void signOut().then(() => navigate('/'));
        },
        icon: <Icon name="log-out" className="h-4 w-4" />,
      },
    ],
    [navigate, signOut, t]
  );

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-ig-border bg-ig-bg-primary px-4 lg:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-9 w-9 border-ig-border bg-transparent p-0 lg:hidden"
          onClick={onToggleSidebar}
          aria-label={t('admin.topBar.toggleSidebar', { defaultValue: 'Toggle admin sidebar' })}
        >
          <Icon name="ellipsis" className="h-4 w-4" />
        </Button>
        <AdminBreadcrumbs />
      </div>
      <div className="flex items-center gap-3">
        <Link
          to="/dashboard"
          className="hidden h-9 items-center rounded-lg bg-ig-accent px-4 font-sans text-sm font-bold text-[var(--ig-accent-text)] transition hover:brightness-110 lg:inline-flex"
        >
          {t('nav.dashboard')}
        </Link>
        <DropdownMenu
          trigger={
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-full"
              aria-label={t('admin.topBar.userMenu', { defaultValue: 'Admin user menu' })}
            >
              <Avatar
                size="md"
                src={user?.avatar ? resolveAvatarSrc(user.avatar) : undefined}
                initials={initials}
                className="h-10 w-10 bg-ig-bg-tertiary"
              />
            </button>
          }
          items={menuItems}
          className="w-[260px]"
        />
      </div>
    </header>
  );
}
